/**
 * Container component for the day parts table.
 */
import { useState, useEffect } from 'react';
import type { TableColumn } from '../../types/TableColumn';
import type { BestWorstValues } from '../../types/BestWorstValues';
import {
  getSelectedRegions,
  getRegionOptions,
} from '../../utils/tables';
import type { DayPartData } from '../../types/DayPartData';
import { getDayPartsData } from '../../utils/api';
import { useData } from '../../hooks/useData';
import TableLoader from './TableLoader';
import TableContainer from './TableContainer';
import TableBody from './TableBody';
import TableHead from './TableHead';
import TableHeader from './TableHeader';
import Table from './Table';
import DropdownRegionMultiple from '../dropdowns/DropDownRegionMuli';
import RefreshButton from '../buttons/RefreshButton';

interface RowType {
  locationName: string;
  [key: string]: string | number;
}

const buttonData = [
  {
    src: '/icons/walker.svg',
    alt: 'Customer Count',
    stat: 'customerCount',
    tooltip: 'Total number of customers served during the day part',
  },
  {
    src: '/icons/clock.svg',
    alt: 'Average Service Time',
    stat: 'averageServiceTime',
    tooltip: 'Average time taken to serve each customer',
  },
  {
    src: '/icons/bullseye.svg',
    alt: 'Deviation From Target',
    stat: 'deviationFromTarget',
    tooltip: 'Difference between actual and target performance metrics',
  },
  {
    src: '/icons/uturn.svg',
    alt: 'Drive Off Count',
    stat: 'driveoffCount',
    tooltip: 'Number of customers who left without completing their order',
  }
];

const tableTitle = 'Day Parts';

export default function DayPartsTable(): JSX.Element {
  const {
    selectedFranchise,
    selectedDates,
    selectedLocations,
    toggled,
    userLocations,
    selectedRegionIds,
    handleSelectedRegionIds,
  } = useData();
  const [dayPartsData, setDayPartsData] = useState<DayPartData[]>([]);
  const [displayData, setDisplayData] = useState<RowType[]>([]);
  const [rows, setRows] = useState<RowType[]>([]);
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [sortColumn, setSortColumn] = useState<TableColumn>({
    label: 'Location',
    type: 'string',
    key: 'locationName',
  });
  const [sortDirection, setSortDirection] = useState<number>(1);
  const [toggledStat, setToggledStat] = useState<string>('averageServiceTime');
  const [bestValues, setBestValues] = useState<BestWorstValues>({});
  const [worstValues, setWorstValues] = useState<BestWorstValues>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [regions, setRegions] = useState<any[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<any[]>([]);
  const [initialLoad, setInitialLoad] = useState<Boolean>(true);
  // for canceling requests and avoiding stale data
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  function handleSelectedRegionsChange(regionId: string): void {
    let newSelectedRegions;
    const regionInSelected = selectedRegions.find((region) => region.id === regionId);
    if (regionInSelected !== undefined) {
      newSelectedRegions = selectedRegions.filter((region) => region.id !== regionId);
      handleSelectedRegionIds(newSelectedRegions, setSelectedRegions);
    } else {
      const region = regions.find((region) => region.id === regionId);
      newSelectedRegions = [...selectedRegions, region];

      // special behavior for "ALL" region
      if (regionId === 'ALL') {
        newSelectedRegions = [ region];
      }
      else {
        newSelectedRegions = newSelectedRegions.filter((r) => r.id !== 'ALL');
      }
      handleSelectedRegionIds(newSelectedRegions, setSelectedRegions);

    }
  }

  async function fetchData(clearDefaultRegions: boolean = false): Promise<void> {
    if (selectedFranchise === null) {
      return;
    }

    setIsLoading(true);

    if (abortController) {
      abortController.abort();
    }

    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    try {
      const data = await getDayPartsData(
      selectedFranchise.id,
      selectedDates[0],
      selectedDates[0],
      userLocations
        .filter((location) => selectedLocations.includes(location))
        .map((location) => location.id),
      toggled,
      { signal: newAbortController.signal }
    );
    setDayPartsData(data);

    // Set selected regions with all regions
    const uniqueRegions = getUniqueRegions(data);
    handleSelectedRegionIds(
      getSelectedRegions(clearDefaultRegions ? [] : selectedRegionIds, uniqueRegions),
      setSelectedRegions
    );

    setIsLoading(false);

    }
    catch (error) {
      if (error.name !== 'AbortError') {
            console.error('Failed to fetch data:', error);
            setIsLoading(false);
        }
    }
  }

  useEffect(() => {
    fetchData(true).catch((err) => {
      console.error(err);
    }).finally(() => {
      setInitialLoad(false);
    });
  }, []);

  useEffect(() => {
     if (selectedFranchise === undefined || initialLoad)
    {
      return;
    }
    fetchData(true).catch((err) => {
      console.error(err);
    });
  }, [selectedFranchise, toggled,selectedDates[0], selectedLocations]);

  useEffect(() => {
    if (selectedFranchise === undefined || selectedLocations.length === 0 || initialLoad)
    {
      return;
    }
    fetchData(false).catch((err) => {
      console.error(err);
    });
  }, [selectedDates[0], selectedLocations]);

  useEffect(() => {
    const uniqueRegions = getUniqueRegions(dayPartsData);
    setRegions(getRegionOptions(uniqueRegions));
    const allRegion = regions.find(r => r.id === "ALL");
    if (allRegion !== undefined && selectedRegions.length == regions.length) {
      setSelectedRegions([allRegion])
    }
    const newData = getRows(dayPartsData);
    setRows(newData);
    const newColumns = getColumns(dayPartsData);
    setColumns(newColumns);
    const newBestValues: BestWorstValues = {};
    const newWorstValues: BestWorstValues = {};

    for (const col of newColumns) {
      if (col.type === 'string') continue;
      const values = newData
        .map((row) => row[col.key])
        .filter((v): v is number => typeof v === 'number');
      if (values.length === 0) continue;
      const minVal = Math.min(...values);
      const maxVal = Math.max(...values);
      newBestValues[col.key] =
        col.type === 'seconds' ? minVal : maxVal;
      newWorstValues[col.key] =
        col.type === 'seconds' ? maxVal : minVal;
    }

    setBestValues(newBestValues);
    setWorstValues(newWorstValues);
    handleSort(newData, newColumns[0], -1);
  }, [toggledStat, dayPartsData, selectedRegions]);

  // Set the data to display based on the selected locations.
  useEffect(() => {
    const selected = selectedLocations.map((location) => location.displayName.trim());
    const filteredRows = rows.filter((row) => selected.includes(row.locationName.trim()));
    setDisplayData(filteredRows);
  }, [rows, selectedLocations]);

  function getUniqueRegions(rawData: DayPartData[]): Record<string, string> {
    if (rawData.length === 0) return {};
    // Set selected regions with all regions
    const uniqueRegions: Record<string, string> = {};
    // Get all possible regions.
    for (const d of rawData) {
      for (const dp of d.dayParts) {
        uniqueRegions[dp.regionKey] = dp.regionName;
      }
    }

    return uniqueRegions;
  }

  // Get the rows from the data.
  function getRows(rawData: DayPartData[]): RowType[] {
    if (rawData.length === 0) return [];
    const knownDayParts = ['Breakfast', 'Before Lunch', 'Lunch', 'Snack', 'Afternoon', 'Dinner', 'After Dinner', 'Evening', 'Late Night'];
    const allDayPartNames = new Set<string>();
    for (const location of rawData) {
      for (const dayPart of location.dayParts) {
        allDayPartNames.add(dayPart.name);
      }
    }
    const orderedDayPartNames = knownDayParts.filter(name => allDayPartNames.has(name));
    const extraDayPartNames = Array.from(allDayPartNames)
      .filter(name => !knownDayParts.includes(name))
      .sort();
    const finalDayPartNames = [...orderedDayPartNames, ...extraDayPartNames];
    const rows: RowType[] = [];
    for (const location of rawData) {
      const row: RowType = {
        locationName: location.locationName,
      };
      for (const dayPart of location.dayParts) {
        if (selectedRegions.map(r => r.id).includes(dayPart.regionKey)) {
          if (!row[dayPart.name]) {
            row[dayPart.name] = 0;
          }
          if (toggledStat === 'customerCount' || toggledStat === 'driveoffCount') {
            row[dayPart.name] = dayPart[toggledStat];
          } else {
            row[dayPart.name] = row[dayPart.name] + dayPart[toggledStat];
          }
        }
        if (selectedRegions.find(r => r.id === 'ALL')) {
          const allRegion = location.dayParts.filter(dp=> dp.name === dayPart.name).find(dp => dp.regionKey === "ALL");
          if (allRegion) {
            allRegion.deviationFromTarget = Math.min(...location.dayParts.filter(dp=> dp.name === dayPart.name).map(dp => dp.deviationFromTarget));
            allRegion.driveoffCount = Math.max(...location.dayParts.filter(dp=> dp.name === dayPart.name).map(dp => dp.driveoffCount));
          }
        }
      }
      for (const dayPart of finalDayPartNames) {
        if (row[dayPart] === undefined || row[dayPart] === null) {
          row[dayPart] = '--';
        }
      }
      rows.push(row);
    }
    return rows;
  }

  // Get the columns from the data.
  function getColumns(rawData: DayPartData[]): TableColumn[] {
    if (rawData.length === 0) return [];

    const knownDayParts = ['Breakfast', 'Before Lunch', 'Lunch', 'Snack', 'Afternoon', 'Dinner', 'After Dinner', 'Evening', 'Late Night'];
    const allDayPartNames = new Set<string>();
    for (const location of rawData) {
      for (const dayPart of location.dayParts) {
        allDayPartNames.add(dayPart.name);
      }
    }
    const orderedDayPartNames = knownDayParts.filter(name => allDayPartNames.has(name));
    const extraDayPartNames = Array.from(allDayPartNames)
      .filter(name => !knownDayParts.includes(name))
      .sort();
    const finalDayPartNames = [...orderedDayPartNames, ...extraDayPartNames];

    const cols = [
      {
        label: 'Location',
        type: 'string',
        key: 'locationName',
      },
    ];
    for (const dayPart of finalDayPartNames) {
      cols.push({
        label: dayPart,
        type: ['customerCount', 'driveoffCount'].includes(toggledStat) ? 'number' : 'seconds',
        key: dayPart,
      });
    }
    return cols;
  }

  // Sort the data by the given column.
  function handleSort(
    rows: RowType[],
    column: TableColumn,
    sortDirection: number
  ): void {
    const sortedData = [...rows].sort((a, b) => {
      let va: any = a[column.key];
      let vb: any = b[column.key];
      if (va === '--') va = Infinity;
      if (vb === '--') vb = Infinity;
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDirection * (va - vb);
      } else if (typeof va === 'string' && typeof vb === 'string') {
        return sortDirection * va.localeCompare(vb);
      }
      return 0;
    });
    setRows(sortedData);
    setSortColumn(column);
    setSortDirection(sortDirection * -1);
  }

  if (selectedFranchise === null || isLoading) {
    return (
      <TableLoader
        columns={['Breakfast', 'Lunch', 'Snack', 'Dinner', 'Evening']}
        title={tableTitle}
        handleRefresh={() => {
          fetchData().catch((err) => {
            console.error(err);
          });
        }}
      />
    );
  }

  return (
    <TableContainer dataCy="day-parts-table">
      <TableHeader title={tableTitle}>
        <div className="flex items-center gap-12">
          <div className="flex items-center gap-2">
            {buttonData.map((button, index) => (
              <div key={index} className="relative group">
                <button
                  onClick={() => {
                    setToggledStat(button.stat);
                  }}
                  className={`btn p-1 ${
                    toggledStat === button.stat
                      ? 'bg-white shadow-sm dark:bg-slate-700'
                      : 'bg-gray-100 dark:bg-slate-800'
                  } focus:outline-none`}
                  data-cy={`${button.stat}-toggle`}
                >
                  <img src={button.src} alt={button.alt} />
                </button>
                <div className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 -top-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap z-10">
                  {button.tooltip}
                </div>
              </div>
            ))}
          </div>
          <DropdownRegionMultiple
            regionStats={regions}
            selectedRegionStats={selectedRegions}
            handlSelectedRegionStatsChange={handleSelectedRegionsChange}
          />
          <RefreshButton
            handleRefresh={() => {
              fetchData().catch((err) => {
                console.error(err);
              });
            }}
            dataCy="refresh-button-day-parts"
          />
        </div>
      </TableHeader>
      <div className="flex flex-wrap gap-4 p-4 bg-white dark:bg-[#182235]">
        <div className="btn text-sm text-white bg-green-500 p-0 px-2">Best</div>
        <div className="btn text-sm text-white bg-red-500 p-0 px-2">Worst</div>
      </div>
      <Table>
        <TableHead
          columns={columns}
          handleSort={(col) => {
            handleSort(rows, col, sortDirection);
          }}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
        />
        <TableBody
          columns={columns}
          data={displayData}
          bestValues={bestValues}
          worstValues={worstValues}
        />
      </Table>
    </TableContainer>
  );
}